<template>
  <!-- 화면 전체를 덮는 레트로 다마고치 케어 다이얼로그. 사이드바 펫 클릭으로 열림. -->
  <v-dialog
    :value="value"
    width="80vw"
    content-class="care-dialog"
    transition="dialog-bottom-transition"
    overlay-color="#000"
    :overlay-opacity="0.72"
    @input="$emit('input', $event)"
  >
    <div class="care-backdrop">
      <!-- 닫기 -->
      <button class="care-close" :title="$t('care.close')" @click="close">
        <v-icon color="#e9e3ff" :size="26">mdi-close</v-icon>
      </button>

      <!-- 다마고치 기기 -->
      <div class="tama">
        <div class="tama__brand">VIRTU&middot;PET</div>

        <!-- LCD 화면 베젤 -->
        <div class="tama__bezel">
          <div class="tama__screen">
            <!-- 상태창: 무드 + 이름 -->
            <div class="tama__hud">
              <span class="tama__mood">{{ mood.emoji }}</span>
              <span class="tama__name">{{ virtupetName || "펫" }}</span>
            </div>

            <!-- 3D 펫 본체 (소켓 없음, $refs로만 구동) -->
            <div class="tama__model">
              <BroadcastBox ref="broadcastBox" :sources="[]" :size="screenSize" />
            </div>

            <!-- 로딩 -->
            <div v-if="loading" class="tama__loading">
              <v-progress-circular indeterminate color="#3c4a2a" size="28" width="3" />
            </div>

            <!-- 액션 피드백: 둥둥 떠오르는 이모지 -->
            <div class="tama__floats">
              <span v-for="f in floats" :key="f.id" class="tama__float">{{ f.emoji }}</span>
            </div>

            <!-- 레트로 도트/스캔 오버레이 -->
            <div class="tama__scan"></div>
          </div>
        </div>

        <!-- 상태 미터 (배고픔 / 에너지) — 세그먼트 픽셀바 -->
        <div class="tama__meters">
          <div class="tama__meter">
            <span class="tama__meter-label">🍖 {{ $t("care.hunger") }}</span>
            <span class="tama__segs">
              <i
                v-for="n in 10"
                :key="'h' + n"
                class="tama__seg"
                :class="{ 'is-on': n <= segs(hungerPct), 'is-hunger': n <= segs(hungerPct) }"
              ></i>
            </span>
          </div>
          <div class="tama__meter">
            <span class="tama__meter-label">⚡ {{ $t("care.energy") }}</span>
            <span class="tama__segs">
              <i
                v-for="n in 10"
                :key="'e' + n"
                class="tama__seg"
                :class="{ 'is-on': n <= segs(energyPct), 'is-energy': n <= segs(energyPct) }"
              ></i>
            </span>
          </div>
          <!-- 졸림: awake_meter(말짱함, 가득=안 졸림). 라벨만 졸림(care.awake) -->
          <div class="tama__meter">
            <span class="tama__meter-label">💤 {{ $t("care.awake") }}</span>
            <span class="tama__segs">
              <i
                v-for="n in 10"
                :key="'a' + n"
                class="tama__seg"
                :class="{ 'is-on': n <= segs(awakePct), 'is-awake': n <= segs(awakePct) }"
              ></i>
            </span>
          </div>
        </div>

        <!-- 컨트롤: 하드웨어 버튼 3개 -->
        <div class="tama__buttons">
          <button
            v-for="btn in buttons"
            :key="btn.key"
            class="tama__btn"
            :disabled="!modelReady || busy"
            @click="onAction(btn)"
          >
            <span class="tama__btn-emoji">{{ btn.emoji }}</span>
            <span class="tama__btn-label">{{ $t("care." + btn.key) }}</span>
          </button>
        </div>
      </div>
    </div>
  </v-dialog>
</template>

<script>
import { mapState } from "vuex";
import BroadcastBox from "@/components-model/BroadcastBox/BroadcastBox.vue";
import SupabaseApi from "@/api/SupabaseApi";
import { resolveMood } from "@/constants/petMood";
import APP_CAMERA_POSITIONS from "@/constants/APP_CAMERA_POSITIONS";
import delay from "@/utils/delay";

export default {
  name: "PetCareDialog",
  components: { BroadcastBox },
  props: {
    value: { type: Boolean, default: false },
  },
  data() {
    return {
      screenSize: 240,
      loading: false,
      modelReady: false,
      busy: false,
      meters: {
        hunger: 0,
        hungerMax: 100,
        energy: 0,
        energyMax: 100,
        mood: 0,
        moodMax: 100,
        awake: 0,
        awakeMax: 100,
      },
      moodMap: [], // ssot.PET_MOOD_MAP 버킷 배열
      floats: [],
      floatSeq: 0,
      // 컨트롤 버튼 → 펫 리액션 애니메이션 매핑 (영구 미터 변경은 백엔드 워커 연결 후)
      buttons: [
        { key: "feed", emoji: "🍖", anim: "action-only--happy" },
        { key: "play", emoji: "🎾", anim: "action-only--agree" },
        { key: "clean", emoji: "🫧", anim: "action-only--thankyou" },
      ],
    };
  },
  computed: {
    ...mapState(["virtupetName"]),
    mood() {
      return resolveMood(this.meters.mood, this.meters.moodMax, this.moodMap);
    },
    hungerPct() {
      const m = this.meters;
      return m.hungerMax > 0 ? Math.round((m.hunger / m.hungerMax) * 100) : 0;
    },
    energyPct() {
      const m = this.meters;
      return m.energyMax > 0 ? Math.round((m.energy / m.energyMax) * 100) : 0;
    },
    // awake_meter는 '말짱함'(high=good) → 바가 가득하면 안 졸림. 라벨만 '졸림'(care.awake).
    awakePct() {
      const m = this.meters;
      return m.awakeMax > 0 ? Math.round((m.awake / m.awakeMax) * 100) : 0;
    },
  },
  watch: {
    value(open) {
      if (open) this.onOpen();
      else this.modelReady = false;
    },
  },
  methods: {
    // 0~100% → 0~10 세그먼트
    segs(pct) {
      return Math.round((pct / 100) * 10);
    },
    close() {
      this.$emit("input", false);
    },
    async onOpen() {
      this.modelReady = false;
      this.loading = true;
      try {
        // mood맵/미터는 즉시 로드(가벼움)
        const [moodMap, meters] = await Promise.all([
          SupabaseApi.PetStatus.fetchMoodMap(),
          SupabaseApi.PetStatus.fetchMyMeters(),
        ]);
        this.moodMap = moodMap;
        this.meters = meters;

        // 다이얼로그 콘텐츠 마운트 후 3D 펫 로드
        await this.$nextTick();
        await delay(150);
        await this.loadModel();
      } catch (e) {
        console.error("PetCareDialog open failed", e);
      } finally {
        this.loading = false;
      }
    },
    async loadModel() {
      const box = this.$refs.broadcastBox;
      if (!box) return;
      const modelIndex = await SupabaseApi.PetProfile.fetchModelIndex();
      if (!modelIndex) return;
      await box.init(modelIndex, APP_CAMERA_POSITIONS.HOME_ZOOM);
      await delay(200);
      box.startIdle("action-only--{{loop}}idle-40");
      this.modelReady = true;
    },
    async onAction(btn) {
      if (!this.modelReady || this.busy) return;
      this.busy = true;
      this.spawnFloat(btn.emoji);
      try {
        this.$refs.broadcastBox.executeInstruction(btn.anim);
      } catch (e) {
        console.error("care action failed", e);
      }
      // 리액션이 끝나면 다시 idle (연타 방지 짧은 잠금)
      await delay(1400);
      try {
        if (this.modelReady) this.$refs.broadcastBox.startIdle("action-only--{{loop}}idle-40");
      } catch (e) {
        // ignore
      }
      this.busy = false;
    },
    // 화면 위로 떠오르는 이모지 피드백
    spawnFloat(emoji) {
      const id = ++this.floatSeq;
      this.floats.push({ id, emoji });
      setTimeout(() => {
        this.floats = this.floats.filter((f) => f.id !== id);
      }, 1200);
    },
  },
};
</script>

<style scoped>
/* 다이얼로그 콘텐츠 래퍼 — 기본 흰 배경/그림자 제거하고 둥근 패널과 정렬 */
>>> .care-dialog {
  border-radius: 28px;
  box-shadow: none;
  overflow: visible;
}

/* 화면의 ~80%만 덮는 아늑한 다크 패널 — 기기가 떠 보이게 (나머지는 다이얼로그 오버레이로 딤) */
.care-backdrop {
  position: relative;
  width: 100%;
  height: 85vh;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 28px;
  background: radial-gradient(circle at 50% 28%, #2c2746 0%, #16121f 65%, #0d0a14 100%);
  box-shadow: 0 24px 70px rgba(0, 0, 0, 0.6);
  overflow: auto;
}

.care-close {
  position: absolute;
  top: 16px;
  right: 18px;
  z-index: 10;
  width: 42px;
  height: 42px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.14);
  cursor: pointer;
  transition: background 0.15s ease;
}
.care-close:hover {
  background: rgba(255, 255, 255, 0.16);
}

/* ===== 기기 본체 (달걀형 셸) ===== */
.tama {
  position: relative;
  width: 340px;
  margin: 40px 16px;
  padding: 26px 26px 32px;
  display: flex;
  flex-direction: column;
  align-items: center;
  /* 달걀/조약돌 실루엣 */
  border-radius: 46% 46% 42% 42% / 40% 40% 60% 60%;
  background: linear-gradient(157deg, #c9b3ff 0%, #9a78e0 52%, #7c4dff 100%);
  box-shadow: 0 26px 60px rgba(0, 0, 0, 0.55), inset 0 3px 6px rgba(255, 255, 255, 0.5),
    inset 0 -10px 24px rgba(70, 40, 130, 0.45);
}
/* 셸 상단 광택 */
.tama::before {
  content: "";
  position: absolute;
  top: 12px;
  left: 22%;
  width: 56%;
  height: 30px;
  border-radius: 50%;
  background: radial-gradient(ellipse at 50% 0, rgba(255, 255, 255, 0.6), rgba(255, 255, 255, 0));
  pointer-events: none;
}

.tama__brand {
  font-family: maplestory, sans-serif;
  font-size: 13px;
  letter-spacing: 2px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.92);
  text-shadow: 0 1px 1px rgba(80, 45, 140, 0.6);
  margin-bottom: 12px;
}

/* ===== LCD 베젤 + 화면 ===== */
.tama__bezel {
  padding: 16px;
  border-radius: 28px;
  background: linear-gradient(160deg, #4a4364, #2c2740);
  box-shadow: inset 0 3px 8px rgba(0, 0, 0, 0.6), 0 2px 0 rgba(255, 255, 255, 0.25);
}
.tama__screen {
  position: relative;
  width: 240px;
  height: 240px;
  border-radius: 14px;
  overflow: hidden;
  background: radial-gradient(circle at 50% 40%, #b9c79a 0%, #9aac74 70%, #8a9c64 100%);
  box-shadow: inset 0 0 0 2px rgba(0, 0, 0, 0.25), inset 0 6px 16px rgba(60, 74, 42, 0.5);
}
.tama__model {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}
.tama__hud {
  position: absolute;
  top: 6px;
  left: 8px;
  right: 8px;
  z-index: 3;
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: maplestory, sans-serif;
  color: #3a472a;
}
.tama__mood {
  font-size: 18px;
  line-height: 1;
}
.tama__name {
  font-size: 13px;
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.tama__loading {
  position: absolute;
  inset: 0;
  z-index: 4;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* 도트매트릭스 + 비네트 오버레이 */
.tama__scan {
  position: absolute;
  inset: 0;
  z-index: 5;
  pointer-events: none;
  background-image: radial-gradient(rgba(60, 74, 42, 0.16) 1px, transparent 1.4px),
    radial-gradient(circle at 50% 50%, transparent 60%, rgba(40, 52, 26, 0.35) 100%);
  background-size: 4px 4px, 100% 100%;
}

/* 떠오르는 이모지 피드백 */
.tama__floats {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 24px;
  z-index: 6;
  display: flex;
  justify-content: center;
  pointer-events: none;
}
.tama__float {
  position: absolute;
  font-size: 30px;
  animation: tama-float-up 1.2s ease-out forwards;
}
@keyframes tama-float-up {
  0% {
    transform: translateY(0) scale(0.6);
    opacity: 0;
  }
  25% {
    opacity: 1;
    transform: translateY(-14px) scale(1.1);
  }
  100% {
    transform: translateY(-90px) scale(1);
    opacity: 0;
  }
}

/* ===== 미터 (세그먼트 픽셀바) ===== */
.tama__meters {
  width: 240px;
  margin-top: 18px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.tama__meter {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}
.tama__meter-label {
  font-family: maplestory, sans-serif;
  font-size: 12px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.95);
  text-shadow: 0 1px 1px rgba(70, 40, 130, 0.5);
  white-space: nowrap;
}
.tama__segs {
  display: flex;
  gap: 2px;
}
.tama__seg {
  width: 9px;
  height: 14px;
  border-radius: 2px;
  background: rgba(40, 24, 80, 0.35);
  box-shadow: inset 0 1px 1px rgba(0, 0, 0, 0.3);
}
.tama__seg.is-on.is-hunger {
  background: #ffb24d;
  box-shadow: 0 0 4px rgba(255, 178, 77, 0.8);
}
.tama__seg.is-on.is-energy {
  background: #ffe14d;
  box-shadow: 0 0 4px rgba(255, 225, 77, 0.8);
}
.tama__seg.is-on.is-awake {
  background: #7fb3ff;
  box-shadow: 0 0 4px rgba(127, 179, 255, 0.8);
}

/* ===== 하드웨어 버튼 ===== */
.tama__buttons {
  margin-top: 22px;
  display: flex;
  gap: 22px;
}
.tama__btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
  border: none;
  background: transparent;
  cursor: pointer;
}
.tama__btn-emoji {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  background: linear-gradient(160deg, #fff, #e7ddff);
  box-shadow: 0 5px 0 #6f51b8, 0 8px 14px rgba(0, 0, 0, 0.35), inset 0 2px 3px rgba(255, 255, 255, 0.9);
  transition: transform 0.08s ease, box-shadow 0.08s ease;
}
.tama__btn:active:not(:disabled) .tama__btn-emoji {
  transform: translateY(4px);
  box-shadow: 0 1px 0 #6f51b8, 0 3px 6px rgba(0, 0, 0, 0.35), inset 0 2px 3px rgba(255, 255, 255, 0.9);
}
.tama__btn:disabled {
  cursor: default;
  opacity: 0.55;
}
.tama__btn-label {
  font-family: maplestory, sans-serif;
  font-size: 11px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.95);
  text-shadow: 0 1px 1px rgba(70, 40, 130, 0.5);
}

@media (prefers-reduced-motion: reduce) {
  .tama__float {
    animation-duration: 0.6s;
  }
}
</style>
